
import { supabase } from "@/integrations/supabase/client";
import { Shop, CategoryShopRelation } from "@/types";
import logger from '@/utils/logger';
import { queryClient } from "@/services/queryClient";
import { offlineDb } from "@/db/offlineDb";
import { networkService } from '@/services/network.service';
import { syncService } from '@/services/sync.service';
import OfflineLogger from '@/utils/offlineLogger';

// Get all shops for the current user
export const getUserShops = async (): Promise<Shop[]> => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError) {
      logger.logError('shops.service', 'Error getting current user:', {additionalInfo: userError});
      throw userError;
    }

    if (!userData.user) {
      throw new Error('User must be authenticated');
    }

    const { data, error } = await supabase
      .from('user_shops')
      .select('*')
      .eq('user_id', userData.user.id)
      .order('shop_name', { ascending: true });

    if (error) {
      logger.logError('shops.service', 'Error retrieving shops:', {additionalInfo: error});
      throw error;
    }

    return data || [];
  } catch (err) {
    logger.logError('shops.service', 'Error in getUserShops:', {additionalInfo: err});
    throw err;
  }
};

// Get a specific shop by ID
export const getShopById = async (shopId: string): Promise<Shop | null> => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError) {
      logger.logError('shops.service', 'Error getting current user:', {additionalInfo: userError});
      throw userError;
    }

    if (!userData.user) {
      throw new Error('User must be authenticated');
    }

    const { data, error } = await supabase
      .from('user_shops')
      .select('*')
      .eq('id', shopId)
      .eq('user_id', userData.user.id)
      .single();

    if (error) {
      logger.logError('shops.service', `Error retrieving shop ${shopId}:`, {additionalInfo: error});
      throw error;
    }

    return data;
  } catch (err) {
    logger.logError('shops.service', `Error in getShopById for ${shopId}:`, {additionalInfo: err});
    throw err;
  }
};

// Create a new shop
export const createShop = async (shop: Omit<Shop, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<Shop> => {
  try {
    // Assicuriamoci che user_id sia impostato esplicitamente
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      logger.logError('shops.service', 'Error getting current user:', {additionalInfo: userError});
      throw userError;
    }
    
    // Assicurati che l'utente sia autenticato prima di procedere
    if (!userData.user) {
      throw new Error('User must be authenticated to create shops');
    }
    
    const shopWithUserId = {
      ...shop,
      user_id: userData.user.id
    };
    
    const { data, error } = await supabase
      .from('user_shops')
      .insert([shopWithUserId])
      .select()
      .single();

    if (error) {
      logger.logError('shops.service', 'Error creating shop:', {additionalInfo: error});
      throw error;
    }

    return data;
  } catch (err) {
    logger.logError('shops.service', 'Error in createShop:', {additionalInfo: err});
    throw err;
  }
};

// Update a shop
export const updateShop = async (shopId: string, updates: Partial<Shop>): Promise<Shop> => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError) {
      logger.logError('shops.service', 'Error getting current user:', {additionalInfo: userError});
      throw userError;
    }

    if (!userData.user) {
      throw new Error('User must be authenticated');
    }

    const { data, error } = await supabase
      .from('user_shops')
      .update(updates)
      .eq('id', shopId)
      .eq('user_id', userData.user.id)
      .select()
      .single();

    if (error) {
      logger.logError('shops.service', `Error updating shop ${shopId}:`, {additionalInfo: error});
      throw error;
    }

    return data;
  } catch (err) {
    logger.logError('shops.service', `Error in updateShop for ${shopId}:`, {additionalInfo: err});
    throw err;
  }
};

// Delete a shop
export const deleteShop = async (shopId: string): Promise<Shop | null> => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError) {
      logger.logError('shops.service', 'Error getting current user:', {additionalInfo: userError});
      throw userError;
    }

    if (!userData.user) {
      throw new Error('User must be authenticated');
    }

    // Fetch shop before deletion
    const { data: shopData, error: fetchError } = await supabase
      .from('user_shops')
      .select('*')
      .eq('id', shopId)
      .eq('user_id', userData.user.id)
      .single();

    if (fetchError) {
      logger.logError('shops.service', `Error fetching shop ${shopId} before delete:`, {additionalInfo: fetchError});
      return null;
    }

    const { error } = await supabase
      .from('user_shops')
      .delete()
      .eq('id', shopId)
      .eq('user_id', userData.user.id);

    if (error) {
      logger.logError('shops.service', `Error deleting shop ${shopId}:`, {additionalInfo: error});
      throw error;
    }

    return shopData as Shop;
  } catch (err) {
    logger.logError('shops.service', `Error in deleteShop for ${shopId}:`, {additionalInfo: err});
    throw err;
  }
};

// Get category ordering for a specific shop
export const getCategoryOrderingForShop = async (shopId: string): Promise<CategoryShopRelation[]> => {
  try {
    const { data, error } = await supabase
      .from('user_categories_shops')
      .select('*')
      .eq('shop_id', shopId)
      .order('position', { ascending: true });

    if (error) {
      logger.logError('shops.service', `Error retrieving category ordering for shop ${shopId}:`, {additionalInfo: error});
      throw error;
    }

    logger.warn(`Category ordering for shop ${shopId}:`, data);
    return data || [];
  } catch (err) {
    logger.logError('shops.service', `Error in getCategoryOrderingForShop for ${shopId}:`, {additionalInfo: err});
    throw err;
  }
};

// Update category ordering for a shop
export const updateCategoryOrdering = async (
  shopId: string,
  categoryOrderings: { category_id: string; position: number }[]
): Promise<boolean> => {
  try {
    const { data: existingOrderings, error: fetchError } = await supabase
      .from('user_categories_shops')
      .select('*')
      .eq('shop_id', shopId);

    if (fetchError) {
      logger.logError('shops.service', `Error fetching category orderings for shop ${shopId}:`, {additionalInfo: fetchError});
      throw fetchError;
    }

    const updates = [];
    const timestamp = Date.now();

    for (const ordering of categoryOrderings) {
      const existingOrdering = existingOrderings?.find(
        eo => eo.category_id === ordering.category_id
      );

      if (existingOrdering) {
        // Update on Supabase
        updates.push(
          supabase
            .from('user_categories_shops')
            .update({ position: ordering.position })
            .eq('id', existingOrdering.id)
        );

        // ✅ Also update IndexedDB
        await offlineDb.categoryShopOrdering.update(existingOrdering.id, {
          position: ordering.position,
          visibile: existingOrdering.visibile !== false,
          _lastModified: timestamp,
          _syncStatus: 'synced'
        });
      } else {
        // Insert on Supabase
        const { data: userData } = await supabase.auth.getUser();
        const newEntry = {
          shop_id: shopId,
          category_id: ordering.category_id,
          position: ordering.position,
          user_id: userData.user?.id,
          visibile: true
        };

        updates.push(
          supabase
            .from('user_categories_shops')
            .insert([newEntry])
        );

        // ✅ Also insert into IndexedDB
        await offlineDb.categoryShopOrdering.put({
          ...newEntry,
          _syncStatus: 'synced',
          _lastModified: timestamp
        });
      }
    }

    await Promise.all(updates);

    // ✅ Invalidate cache so components get latest data
    await queryClient.invalidateQueries({
      queryKey: ['shop-category-ordering', shopId]
    });

    logger.info(`updateCategoryOrdering completato per shop ${shopId}`);
    return true;
  } catch (err) {
    logger.logError('shops.service', `Error in updateCategoryOrdering for shop ${shopId}:`, {additionalInfo: err});
    throw err;
  }
};

// Associate a shopping list with a shop
export const associateListWithShop = async (
  listId: string,
  shopId: string | null
): Promise<boolean> => {
  const isOnline = networkService.isOnline();

  if (isOnline) {
    try {
      const { error } = await supabase
        .from('shopping_lists')
        .update({ shop_id: shopId })
        .eq('id', listId);

      if (error) {
        logger.logError('shops.service', `Error associating list ${listId} with shop ${shopId}:`, {additionalInfo: error});
        throw error;
      }

      return true;
    } catch (err: any) {
      logger.logError('shops.service', `Error in associateListWithShop for list ${listId} and shop ${shopId}:`, {additionalInfo: err});

      if (err?.message?.includes('Failed to fetch')) {
        try {
          await offlineDb.shoppingLists.update(listId, {
            shop_id: shopId,
            updated_at: new Date().toISOString(),
            _syncStatus: 'pending',
            _lastModified: Date.now()
          });

          await syncService.addToSyncQueue('shoppingLists', 'update', listId, {
            shop_id: shopId,
            updated_at: new Date().toISOString()
          });

          OfflineLogger.info('shops.service.associateListWithShop', `🟠 queued association of list ${listId} with shop ${shopId} due to network error`);
          return true;
        } catch (offlineErr) {
          OfflineLogger.error('shops.service.associateListWithShop.offlineFallback', offlineErr);
          return false;
        }
      }

      return false;
    }
  }

  // Offline branch
  try {
    await offlineDb.shoppingLists.update(listId, {
      shop_id: shopId,
      updated_at: new Date().toISOString(),
      _syncStatus: 'pending',
      _lastModified: Date.now()
    });

    await syncService.addToSyncQueue('shoppingLists', 'update', listId, {
      shop_id: shopId,
      updated_at: new Date().toISOString()
    });

    OfflineLogger.info('shops.service.associateListWithShop', `🟠 queued association of list ${listId} with shop ${shopId}`);
    return true;
  } catch (offlineErr) {
    OfflineLogger.error('shops.service.associateListWithShop.offline', offlineErr);
    return false;
  }
};
